import { useNewCode } from '@calendly/feature-check-react';
import { SelectField, useFormContext } from '@calendly/form';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { ArrowExpandIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import classNames from 'classnames';
import { useCallback, useState, useRef, forwardRef } from 'react';
import { SuggestTimes } from 'components/suggest_times';
import { trackToggleRichTextEditorToolbar } from 'features/send_from_calendly';
import { useTrack } from 'hooks/analytics_events';
import { insertHtmlAtCursor, getQuillEditor } from 'shared/utils';
import { EmailSubjectField } from './email-subject-field';
import { RichTextBodyField } from './rich-text-body-field';
import { SendFromOption } from './send_from_option';
import { FieldBlock } from './styles';
const containerCss = "czwxlqy";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const helpTextContainerCss = "h1t1i6mj";
const HelpTextContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(helpTextContainerCss, props.className)} />;
});
const richTextBodyFieldContainerCss = "r1g3spzm";
const RichTextBodyFieldContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(richTextBodyFieldContainerCss, props.className)} />;
});
const IntegrationsLink = ({
  children
}) => <Link target="_blank" rel="noopener noreferrer" href="/integrations?category=email_messaging">
    {children}
    <ArrowExpandIcon inline size="standard" />
  </Link>;
const SetupEmailText = ({
  emails
}) => {
  if (emails.length !== 0) return null;
  return <HelpTextContainer>
      {HtmlParser(I18n.t('publisher.contacts.share.email_form.no_emails'), {
      IntegrationsLink
    })}
    </HelpTextContainer>;
};
const EmailDisconnectionText = ({
  emails,
  values
}) => {
  const emailType = values.send_with.split('_')[0];
  if (emailType === '') return null;
  const selectedEmail = emails.find(email => email.type === emailType);
  if (selectedEmail.accessGranted) return null;
  return <HelpTextContainer>
      {I18n.t(`publisher.integrations.${emailType}.is_disconnected`, {
      email: selectedEmail.emailAddress
    })}
      <br />
      {HtmlParser(I18n.t('publisher.contacts.share.email_form.no_emails'), {
      IntegrationsLink
    })}
    </HelpTextContainer>;
};
export const SendFromCalendlyEmailForm = ({
  emails,
  isLoading,
  eventType,
  userData,
  contact,
  source
}) => {
  const [isToggleToolbar, setToolbarToggle] = useState(true);
  const isSendFromCalendlyAtteEnabled = useNewCode('send_from_calendly_atte')?.enabled;
  const {
    values,
    setFieldError,
    errors: {
      body: bodyError,
      subject: subjectError
    }
  } = useFormContext();
  const editorRef = useRef(null);
  const track = useTrack();
  const {
    id: eventTypeId
  } = eventType ?? {};
  const {
    uuid: contactUuid,
    id: contactId,
    timeZone: contactTimeZone
  } = contact ?? {};
  const clearSubjectError = useCallback(() => {
    setFieldError('subject', '');
  }, [setFieldError]);
  const clearBodyError = useCallback(() => setFieldError('body', ''), [setFieldError]);
  const handleEditorToolbarToggle = useCallback(() => {
    const contactIdentifier = contactUuid ?? contactId;
    setToolbarToggle(currentState => {
      const isToggled = !currentState;
      trackToggleRichTextEditorToolbar({
        track,
        isShown: isToggled,
        eventTypeId,
        contactIdentifier,
        source
      });
      return isToggled;
    });
  }, [eventTypeId, contactUuid, contactId, source, track]);
  const handleAvailabilitySelect = data => {
    if (data?.generatedContent) {
      const {
        html: htmlContent
      } = data.generatedContent;
      const quillEditor = getQuillEditor(editorRef);
      if (quillEditor) {
        insertHtmlAtCursor(quillEditor, htmlContent);
      }
    }
  };
  return <Container>
      <FieldBlock widthLimited>
        <SelectField name="send_with" label={I18n.t('publisher.contacts.from')} disabled={emails.length === 0 || isLoading} options={emails.map(option => ({
        value: `${option.type}_${option.accountUuid}`,
        name: <SendFromOption {...option} />
      }))} />
      </FieldBlock>
      <SetupEmailText emails={emails} />
      <EmailDisconnectionText emails={emails} values={values} />
      <EmailSubjectField name="subject" errorMessage={subjectError} onFocus={clearSubjectError} disabled={isLoading} singleLine />
      <RichTextBodyFieldContainer>
        <RichTextBodyField ref={editorRef} name="body" errorMessage={bodyError} onFocus={clearBodyError} disabled={isLoading} toggleToolbar={isToggleToolbar} />
        {isSendFromCalendlyAtteEnabled && <SuggestTimes eventType={eventType} timeNotation={userData?.time_notation} timezone={contactTimeZone || eventType?.availability_time_zone} handleEditorToolbarToggle={handleEditorToolbarToggle} onAvailabilitySelect={handleAvailabilitySelect} isSendFromCalendly toggleToolbar={isToggleToolbar} contact={contact} source={source} />}
      </RichTextBodyFieldContainer>
    </Container>;
};

require("./send-from-calendly-email-form.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./send-from-calendly-email-form.js");